import React, {useRef, useCallback, useEffect} from 'react';
import {BANG, NUMBER} from 'zobject/data-types.js';
import {EFFECTS} from 'constants/editor-bottom-modes.js';
import {useContainerCoords} from "components/hooks/useContainerCoords.js";
import {
    setShowLFOs,
    setShowEnvelopes,
    setBulkPresentationWindowPositions,
    setBulkWindowPositions,
    setQuickAddSearchText,
    setViewableSubPatches,
    setViewableZObjects,
    setShowSearchWindow} from 'actions/index.js';
import {
    setShowEditorBottom,
    setShowTrackInstrument} from 'actions/index.js';

export const useEditorKeys = ({scrollRef, sequencerEngine, context}) => {
    const {getXY} = useContainerCoords({containerRef: scrollRef});
    const mouseRef = useRef();

    const _getXY = useCallback((e) => {
        let {x,y} = getXY(e);
        let {editorPadding} = sequencerEngine.state.spatial;
        x -= Math.max(0, editorPadding.x);
        y -= Math.max(0, editorPadding.y);
        return {x,y};
    }, [sequencerEngine]);

    const onMouseMove = (e) => {
        if (mouseRef.current) {
            mouseRef.current.clientX = e.clientX;
            mouseRef.current.clientY = e.clientY;
        } else {
            mouseRef.current = {
                clientX: e.clientX,
                clientY: e.clientY
            };
        }
    };

    useEffect(() => {
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("keyup", onKeyUp);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("keyup", onKeyUp);
        };
    }, [sequencerEngine, context, onKeyDown, onKeyUp]);

    const onKeyUp = useCallback((e) => {
        if (e.target.tagName === 'INPUT' ||
            e.target.tagName === 'TEXTAREA') {
            return;
        }

        let key = e.key;
        let {fullScreenZObject} = sequencerEngine.state.zobject;
    }, [sequencerEngine, context]);

    const onKeyDown = useCallback((e) => {
        if (e.target.tagName === 'INPUT' ||
            e.target.tagName === 'TEXTAREA') {
            return;
        }

        let {fullScreenZObject} = sequencerEngine.state.zobject;


        let {key} = e;
        if (key === 'Tab' && !e.shiftKey) {
            if (getContext() !== context) {
                return;
            }
            if (sequencerEngine.state.instruments.showFormantEffect) {
                return;
            }
            sequencerEngine.dispatch(setQuickAddSearchText(""));
            if (sequencerEngine.state.spatial.showTrackInstrument ||
                sequencerEngine.state.sequencer.showLFOs || 
                sequencerEngine.state.sequencer.showEnvelopes) {
                sequencerEngine.dispatch(setShowLFOs(false));
                sequencerEngine.dispatch(setShowEnvelopes(false));
                sequencerEngine.dispatch(
                    setShowTrackInstrument(
                        false));
                return;
            }
            if (sequencerEngine.getSelectedTrack()) {
                sequencerEngine.selectTrack(sequencerEngine.getSelectedTrack().trackNumber);
            }
            sequencerEngine.dispatch(
                setShowTrackInstrument(
                    !sequencerEngine.state.spatial.showTrackInstrument));

        } else if (key === 'e' && (e.ctrlKey || e.metaKey)) {
            if (getContext() !== context) {
                return;
            }
            sequencerEngine.dispatch(
                setShowEditorBottom(sequencerEngine.state.spatial.showEditorBottom === EFFECTS ? undefined : EFFECTS));
        } else if (sequencerEngine.state.spatial.showPadSequencer) {
            return;
        } else if  (key === 'N') {
            if (getContext() !== context) {
                return;
            }
            let {x,y} = _getXY(mouseRef.current);
            getContext().createZObject(x,y);
        } else if (key === 'C') {
            if (getContext() !== context) {
                return;
            }
            let {x,y} = _getXY(mouseRef.current);
            getContext().createZObject(x,y, "comment");
        } else if (key === 'O') {
            if (getContext() !== context) {
                return;
            }
            let {x,y} = _getXY(mouseRef.current);
            getContext().createZMessage(x,y, BANG);
        } else if (key === 'M') {
            if (getContext() !== context) {
                return;
            }
            let {x,y} = _getXY(mouseRef.current);
            getContext().createZMessage(x,y);
        } else if (key === 'H') {
            // horizontally align selected elements
            let {selectedElements} = sequencerEngine.state.windows;
            let {presentationMode} = sequencerEngine.state.spatial;
            let {windowPositions, presentationWindowPositions} = sequencerEngine.state.windows;
            let positions = {};
            for (let elem in selectedElements) {
                let pos = presentationMode ? presentationWindowPositions[elem] || windowPositions[elem] : windowPositions[elem];
                positions[elem] = {
                    ... pos
                };
            }
            let minY = Math.min(... Object.values(positions).map(e => e.y));
            for (let elem in selectedElements) {
                positions[elem].y = minY;
            }
            if (presentationMode) {
                sequencerEngine.dispatch(
                    setBulkPresentationWindowPositions(positions));
            } else {
                sequencerEngine.dispatch(
                    setBulkWindowPositions(positions));
            }
        } else if (key === 'V') {
            // horizontally align selected elements
            let {selectedElements} = sequencerEngine.state.windows;
            let {presentationMode} = sequencerEngine.state.spatial;
            let {windowPositions, presentationWindowPositions} = sequencerEngine.state.windows;
            let positions = {};
            for (let elem in selectedElements) {
                let pos = presentationMode ? presentationWindowPositions[elem] || windowPositions[elem] : windowPositions[elem];
                positions[elem] = {
                    ... pos
                };
            }
            let minX = Math.min(... Object.values(positions).map(e => e.x));
            for (let elem in selectedElements) {
                positions[elem].x = minX;
            }
            if (presentationMode) {
                sequencerEngine.dispatch(
                    setBulkPresentationWindowPositions(positions));
            } else {
                sequencerEngine.dispatch(
                    setBulkWindowPositions(positions));
            }
        } else if (key === 'F') {
            if (getContext() !== context) {
                return;
            }
            let {x,y} = _getXY(mouseRef.current);
            let number = getContext().createZMessage(x,y, NUMBER);
            number.max = 1;
        } else if (key === 'I') {
            if (getContext() !== context) {
                return;
            }
            let {x,y} = _getXY(mouseRef.current);
            let number = getContext().createZMessage(x,y, NUMBER);
            number.round = true;
       } else if (e.key === "Escape") {
            e.preventDefault();
            sequencerEngine.dispatch(
                setViewableSubPatches([]));
            sequencerEngine.dispatch(
                setViewableZObjects([]));
        }
    }, [sequencerEngine, context]);

    const getContext = useCallback(() => {
        let viewing = sequencerEngine.state.zobject.viewableSubPatches;
        return viewing[viewing.length - 1] || sequencerEngine.currentSubSequence;
    }, [sequencerEngine]);

    return {onMouseMove};
};
